import type { StyleRule } from '@vanilla-extract/css';
import { mapValues, omit } from 'lodash-es';

// This function is largely lifted from the Braid Design System
// https://github.com/seek-oss/braid-design-system/blob/master/packages/braid-design-system/src/lib/css/responsiveStyle.ts

type CSSProps = Omit<StyleRule, '@media' | '@supports'>;

export const breakpoints = {
  mobile: 0,
  tablet: 768,
  desktop: 1024,
  wide: 1280,
  extraWide: 1920,
} as const;

export type Breakpoint = keyof typeof breakpoints;

export const breakpointQuery = mapValues(omit(breakpoints, 'mobile'), (bp) => `screen and (min-width: ${bp}px)`);

const makeMediaQuery = (breakpoint: keyof typeof breakpointQuery) => (styles?: CSSProps) =>
  !styles || Object.keys(styles).length === 0
    ? {}
    : {
        [breakpointQuery[breakpoint]]: styles,
      };

const mediaQuery = {
  tablet: makeMediaQuery('tablet'),
  desktop: makeMediaQuery('desktop'),
  wide: makeMediaQuery('wide'),
  extraWide: makeMediaQuery('extraWide'),
};

interface ResponsiveStyle {
  mobile?: CSSProps;
  tablet?: CSSProps;
  desktop?: CSSProps;
  wide?: CSSProps;
  extraWide?: CSSProps;
}

export const responsiveStyle = ({ mobile, tablet, desktop, wide, extraWide }: ResponsiveStyle): StyleRule => ({
  ...omit(mobile, '@media'),
  ...(tablet || desktop || wide || extraWide
    ? {
        '@media': {
          ...mediaQuery.tablet(tablet ?? {}),
          ...mediaQuery.desktop(desktop ?? {}),
          ...mediaQuery.wide(wide ?? {}),
          ...mediaQuery.extraWide(extraWide ?? {}),
        },
      }
    : {}),
});
